import React, { useEffect, useMemo, useState } from 'react';
import {
  PlayerAppearance,
  PlayerSpriteManifestEntry,
  SKIN_TONE_PRESETS,
  VALID_GENDERS,
} from '../types/character';
import { getPlayerBaseSprite, SKIN_TONE_FILTERS } from '../utils/spriteMap';
import './PlayerAppearanceEditor.css';

interface PlayerAppearanceEditorProps {
  race: string;
  value: PlayerAppearance;
  onChange: (next: PlayerAppearance) => void;
}

let manifestCache: PlayerSpriteManifestEntry[] | null = null;

const SKIN_TONE_LABELS: Record<string, string> = {
  default: 'Default',
  pale: 'Pale',
  tan: 'Tan',
  brown: 'Brown',
  dark: 'Dark',
};

const PlayerAppearanceEditor: React.FC<PlayerAppearanceEditorProps> = ({ race, value, onChange }) => {
  const [entries, setEntries] = useState<PlayerSpriteManifestEntry[]>(manifestCache ?? []);
  const [isLoading, setIsLoading] = useState(!manifestCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (manifestCache) return;
    (async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.PUBLIC_URL || ''}/sprites/player-sprite-manifest.json`);
        if (!response.ok) throw new Error('Failed to load sprite manifest');
        const data: PlayerSpriteManifestEntry[] = await response.json();
        manifestCache = data;
        setEntries(data);
      } catch {
        setError('Could not load the appearance library. Try again later.');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const hairOptions = useMemo(() => entries.filter(e => e.category === 'hair'), [entries]);
  const beardOptions = useMemo(() => entries.filter(e => e.category === 'beard'), [entries]);

  const baseSprite = getPlayerBaseSprite(race, value.gender);
  const skinFilter = SKIN_TONE_FILTERS[value.skinTone] ?? undefined;

  const setField = <K extends keyof PlayerAppearance>(key: K, fieldValue: PlayerAppearance[K]) => {
    onChange({ ...value, [key]: fieldValue });
  };

  return (
    <div className="appearance-editor">
      <div className="appearance-preview-stage">
        <img src={baseSprite} alt="Base body" style={skinFilter ? { filter: skinFilter } : undefined} />
        {value.beardSprite && <img src={value.beardSprite} alt="Facial hair" />}
        {value.hairSprite && <img src={value.hairSprite} alt="Hair" />}
      </div>

      <div className="appearance-editor-field">
        <label>Gender</label>
        <div className="appearance-toggle-group">
          {VALID_GENDERS.map(g => (
            <button
              type="button"
              key={g}
              className={`appearance-toggle-btn${value.gender === g ? ' active' : ''}`}
              onClick={() => setField('gender', g)}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <div className="appearance-editor-field">
        <label>Skin Tone</label>
        <div className="skin-tone-swatches">
          {SKIN_TONE_PRESETS.map(tone => (
            <button
              type="button"
              key={tone}
              title={SKIN_TONE_LABELS[tone] ?? tone}
              className={`skin-tone-swatch${value.skinTone === tone ? ' active' : ''}`}
              style={{ filter: SKIN_TONE_FILTERS[tone] }}
              onClick={() => setField('skinTone', tone)}
            />
          ))}
        </div>
      </div>

      {error && <p className="appearance-editor-error">{error}</p>}
      {isLoading && <p className="appearance-editor-loading">Loading appearance options...</p>}

      {!isLoading && !error && (
        <>
          <div className="appearance-editor-field">
            <label>Hair</label>
            <div className="sprite-picker-grid appearance-grid">
              <button
                type="button"
                className={`sprite-picker-item${!value.hairSprite ? ' active' : ''}`}
                onClick={() => setField('hairSprite', null)}
              >
                <span className="appearance-none-swatch">None</span>
              </button>
              {hairOptions.map(entry => (
                <button
                  type="button"
                  key={entry.url}
                  className={`sprite-picker-item${value.hairSprite === entry.url ? ' active' : ''}`}
                  title={entry.name}
                  onClick={() => setField('hairSprite', entry.url)}
                >
                  <img src={entry.url} alt={entry.name} loading="lazy" />
                  <span>{entry.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="appearance-editor-field">
            <label>Facial Hair</label>
            <div className="sprite-picker-grid appearance-grid">
              <button
                type="button"
                className={`sprite-picker-item${!value.beardSprite ? ' active' : ''}`}
                onClick={() => setField('beardSprite', null)}
              >
                <span className="appearance-none-swatch">None</span>
              </button>
              {beardOptions.map(entry => (
                <button
                  type="button"
                  key={entry.url}
                  className={`sprite-picker-item${value.beardSprite === entry.url ? ' active' : ''}`}
                  title={entry.name}
                  onClick={() => setField('beardSprite', entry.url)}
                >
                  <img src={entry.url} alt={entry.name} loading="lazy" />
                  <span>{entry.name}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlayerAppearanceEditor;
